/**
 * Fuzzy matching of Netzplan customer/object names against live records.
 *
 * Ported verbatim (behaviour-for-behaviour) from the validated Node prototype. The
 * scorer combines token containment with a char-level fallback so abbreviations and
 * typos still resolve (IVA ↔ I.V.A., CorPore ↔ CorePore). Ambiguity is never guessed —
 * a low margin surfaces as UNSURE for the user to confirm.
 */

import type {
  LiveCustomer,
  LiveObject,
  LiveService,
  MatchCategory,
  NetzplanCustomer,
  NetzplanObject,
} from './types';

/** Normalize a display string: lowercase, ASCII-fold umlauts, strip punctuation. */
export function norm(value: string): string {
  return String(value ?? '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[.,;:!?'"`´()/\\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const STOP_WORDS = new Set([
  'gmbh', 'co', 'kg', 'og', 'mbh', 'gesmbh', 'ag', 'ges', 'm b h', 'immobilien',
  'immobilienverwaltung', 'immobilientreuhand', 'immobilienverwaltungs', 'liegenschaftsmanagement',
  'gebaeudeverwaltung', 'gebaeudemanagement', 'hausverwaltung', 'verwaltung', 'treuhand',
  'gemeinnuetzige', 'rechtsanwaelte', 'rechtsanwalt', 'wohnbau', 'projektentwicklungs',
  'management', 'support', 'the', 'und', 'gemeinnuetzig', 'wohnungsgesellschaft',
]);

/** Reduce a customer name to its distinctive tokens (drops legal forms / generic words). */
export function customerKey(name: string): string {
  return norm(name)
    .split(' ')
    .filter((t) => t && !STOP_WORDS.has(t))
    .join(' ');
}

function tokens(s: string): string[] {
  return s.split(' ').filter(Boolean);
}

function levenshtein(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: n + 1 }, (_, j) => j);
  for (let i = 1; i <= m; i += 1) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j += 1) {
      const tmp = dp[j];
      dp[j] = Math.min(dp[j] + 1, dp[j - 1] + 1, prev + (a[i - 1] === b[j - 1] ? 0 : 1));
      prev = tmp;
    }
  }
  return dp[n];
}

function charSim(a: string, b: string): number {
  const x = a.replace(/ /g, '');
  const y = b.replace(/ /g, '');
  if (!x || !y) return 0;
  if (x.includes(y) || y.includes(x)) return 0.9;
  const d = levenshtein(x, y);
  return 1 - d / Math.max(x.length, y.length);
}

/** Similarity of two normalized keys in [0,1]: token containment or char fallback. */
export function score(aKey: string, bKey: string): number {
  const A = new Set(tokens(aKey));
  const B = new Set(tokens(bKey));
  if (!A.size || !B.size) return 0;
  let inter = 0;
  A.forEach((t) => {
    if (B.has(t)) inter += 1;
  });
  const smaller = Math.min(A.size, B.size);
  const jacc = inter / (A.size + B.size - inter);
  const contain = inter / smaller;
  const tokenScore = 0.65 * contain + 0.35 * jacc;
  return Math.max(tokenScore, charSim(aKey, bKey));
}

function categoryFor(bestScore: number, matchThreshold: number, unsureThreshold: number): MatchCategory {
  if (bestScore >= matchThreshold) return 'MATCH';
  if (bestScore >= unsureThreshold) return 'UNSURE';
  return 'NEU';
}

export interface CustomerMatch {
  suggestion: LiveCustomer | null;
  score: number;
  category: MatchCategory;
}

/** Best live-customer match for a single raw name (used for bucket children too). */
export function matchCustomerName(rawName: string, live: LiveCustomer[]): CustomerMatch {
  const key = customerKey(rawName);
  let best: LiveCustomer | null = null;
  let bestScore = 0;
  for (const c of live) {
    const s = score(key, customerKey(c.name));
    if (s > bestScore) {
      bestScore = s;
      best = c;
    }
  }
  return { suggestion: best, score: Math.round(bestScore * 100) / 100, category: categoryFor(bestScore, 0.6, 0.34) };
}

export function matchCustomers(
  netzplan: NetzplanCustomer[],
  live: LiveCustomer[],
): Map<string, CustomerMatch> {
  const out = new Map<string, CustomerMatch>();
  for (const nc of netzplan) {
    out.set(nc.key, matchCustomerName(nc.rawNames[0] ?? nc.key, live));
  }
  return out;
}

export interface ObjectMatch {
  suggestion: LiveObject | null;
  score: number;
  category: MatchCategory;
}

/** Best object match within a customer's objects (compares name and street). */
export function matchObject(obj: Pick<NetzplanObject, 'objName'>, liveForCustomer: LiveObject[]): ObjectMatch {
  const nk = norm(obj.objName);
  let best: LiveObject | null = null;
  let bestScore = 0;
  for (const mo of liveForCustomer) {
    const nameScore = score(nk, norm(mo.name));
    const streetScore = score(nk, norm(mo.street));
    const substr = norm(mo.name).includes(nk) || (norm(mo.street) !== '' && nk.includes(norm(mo.street))) ? 0.8 : 0;
    const s = Math.max(nameScore, streetScore, substr);
    if (s > bestScore) {
      bestScore = s;
      best = mo;
    }
  }
  return { suggestion: best, score: Math.round(bestScore * 100) / 100, category: categoryFor(bestScore, 0.6, 0.4) };
}

export interface ServiceMatch {
  suggestion: LiveService | null;
  score: number;
  category: MatchCategory;
}

/**
 * Best live-service match for a free-text service/position title. Reuses the customer
 * scorer (token containment + char fallback) — service names are short noun phrases, so
 * the same normalization applies; only the stop-word key reduction is skipped.
 */
export function matchService(rawName: string, live: LiveService[]): ServiceMatch {
  const key = norm(rawName);
  let best: LiveService | null = null;
  let bestScore = 0;
  for (const s of live) {
    const sc = score(key, norm(s.name));
    if (sc > bestScore) {
      bestScore = sc;
      best = s;
    }
  }
  return { suggestion: best, score: Math.round(bestScore * 100) / 100, category: categoryFor(bestScore, 0.6, 0.4) };
}
